// =============================================================================
// SupportChatWidget — Widget flottant d'aide utilisateur
// Onglets: Chat IA | Base de connaissance | Ticket
// L'IA consulte la KB et crée des tickets routés CxO
// =============================================================================
import { useState, useEffect, useRef } from "react";
import {
  MessageCircleQuestion, X, Send, Ticket, ChevronDown, Trash2,
  ArrowRight, BookOpen, Search, ChevronRight, ArrowLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "technique", label: "Technique / Bug", cxo: "CTO", color: "text-[var(--oh-error-text)]" },
  { id: "commercial", label: "Commercial / Dossiers", cxo: "CSO", color: "text-[var(--oh-accent-text)]" },
  { id: "conformite", label: "Conformité / ANAH", cxo: "CTO", color: "text-[var(--oh-info-text)]" },
  { id: "operations", label: "Opérations / Installateurs", cxo: "COO", color: "text-[var(--oh-success-text)]" },
  { id: "marketing", label: "Marketing / Campagnes", cxo: "CMO", color: "text-[var(--oh-accent-text)]" },
  { id: "general", label: "General", cxo: "CTO", color: "text-[var(--oh-text-secondary)]" },
];

const KB_CATEGORY_LABELS = {
  guide: "Tutos",
  general: "General",
  commercial: "Commercial",
  conformité: "Conformité",
  operations: "Operations",
  technique: "Technique",
  marketing: "Marketing",
  finance: "Finance",
  gouvernance: "Gouvernance",
};

function getHeaders() {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat"); // "chat" | "kb" | "ticket"
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketPriority, setTicketPriority] = useState("medium");
  const [ticketCategory, setTicketCategory] = useState("general");
  const [detectedCategory, setDetectedCategory] = useState(null);
  const messagesEndRef = useRef(null);

  // KB state
  const [kbArticles, setKbArticles] = useState([]);
  const [kbSearch, setKbSearch] = useState("");
  const [kbCategory, setKbCategory] = useState(null);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbSelected, setKbSelected] = useState(null);
  const [kbDetail, setKbDetail] = useState(null);
  const [kbDetailLoading, setKbDetailLoading] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        rôle: "assistant",
        content: "Bonjour ! Je suis l'assistant OmniHub.\n\nPosez-moi votre question, consultez la base de connaissance, ou creez un ticket si besoin.",
      }]);
    }
  }, [open]);

  // ─── Chat ─────────────────────────────────────────────

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { rôle: "user", content: input.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const res = await fetch(`${API}/api/support/chat`, {
        method: "POST",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({ message: userMsg.content, session_id: sessionId }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages((prev) => [...prev, {
          rôle: "assistant",
          content: data.response,
          kb_suggestions: data.kb_suggestions || [],
        }]);
        setSessionId(data.session_id);
        if (data.detected_category) {
          setDetectedCategory(data.detected_category);
          setTicketCategory(data.detected_category);
        }
        // Si l'IA suggère un ticket MAIS qu'on a trouvé des articles BDC,
        // on laisse l'utilisateur les consulter d'abord — pas d'auto-bascule.
        if (data.suggest_ticket && !(data.kb_suggestions || []).length) {
          setShowTicketForm(true);
          setTab("ticket");
        }
      } else {
        setMessages((prev) => [...prev, { rôle: "assistant", content: "Erreur de connexion. Veuillez reessayer." }]);
      }
    } catch {
      setMessages((prev) => [...prev, { rôle: "assistant", content: "Erreur reseau. Veuillez reessayer." }]);
    } finally {
      setSending(false);
    }
  };

  // ─── Ticket ───────────────────────────────────────────

  const createTicket = async () => {
    if (!ticketTitle.trim()) {
      toast.error("Veuillez saisir un titre pour le ticket");
      return;
    }
    const description = messages
      .filter((m) => m.rôle === "user")
      .map((m) => m.content)
      .join("\n---\n");

    const cat = CATEGORIES.find((c) => c.id === ticketCategory) || CATEGORIES[5];

    try {
      const res = await fetch(`${API}/api/support/tickets`, {
        method: "POST",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify({
          title: ticketTitle.trim(),
          description,
          priority: ticketPriority,
          category: ticketCategory,
          session_id: sessionId,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Ticket #${String(data.ticket_numero).padStart(3, "0")} transmis au ${data.cxo_target?.toUpperCase()}`);
        setMessages((prev) => [...prev, {
          rôle: "assistant",
          content: `Ticket #${String(data.ticket_numero).padStart(3, "0")} créé.\nCategorie : ${data.category_label}\nTransmis au : ${data.cxo_target?.toUpperCase()}\nSuivez l'avancement dans Operation Hub > Tickets.`,
        }]);
        setShowTicketForm(false);
        setTicketTitle("");
        setTab("chat");
      } else {
        toast.error(data.detail || "Erreur lors de la creation du ticket");
      }
    } catch {
      toast.error("Erreur reseau");
    }
  };

  // ─── KB ───────────────────────────────────────────────

  const loadKB = async (search, category) => {
    setKbLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (category) params.set("category", category);
      params.set("limit", "30");
      const res = await fetch(`${API}/api/support/kb/articles?${params}`, {
        headers: getHeaders(),
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setKbArticles(data.articles || []);
      }
    } catch {
      setKbArticles([]);
    } finally {
      setKbLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "kb") loadKB(kbSearch, kbCategory);
  }, [tab, kbCategory]);

  const handleKbSearch = () => {
    loadKB(kbSearch, kbCategory);
  };

  const selectArticle = async (art) => {
    setKbSelected(art);
    if (art.slug) {
      setKbDetailLoading(true);
      try {
        const res = await fetch(`${API}/api/kb/article/${art.slug}`, {
          headers: getHeaders(), credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setKbDetail(data);
        } else {
          setKbDetail(art);
        }
      } catch {
        setKbDetail(art);
      } finally {
        setKbDetailLoading(false);
      }
    } else {
      setKbDetail(art);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowTicketForm(false);
    setTicketTitle("");
    setDetectedCategory(null);
  };

  const selectedCat = CATEGORIES.find((c) => c.id === ticketCategory) || CATEGORIES[5];

  // ─── Bulle fermée ─────────────────────────────────────

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-[var(--oh-accent-contrast)] shadow-lg shadow-blue-500/30 flex items-center justify-center hover:scale-110 transition-transform"
        data-testid="support-chat-toggle"
        title="Aide & Support"
      >
        <MessageCircleQuestion className="w-6 h-6" />
      </button>
    );
  }

  // ─── Panel ouvert ─────────────────────────────────────

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[400px] h-[560px] bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-2xl shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
      data-testid="support-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircleQuestion className="w-5 h-5 text-[var(--oh-text)]" />
          <span className="text-sm font-medium text-[var(--oh-text)]">Support OmniHub</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={resetChat} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" title="Nouvelle conversation" data-testid="support-reset-btn">
            <Trash2 className="w-4 h-4 text-[var(--oh-text)]" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" title="Fermer" data-testid="support-close-btn">
            <ChevronDown className="w-4 h-4 text-[var(--oh-text)]" />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-[var(--oh-border)] flex-shrink-0" data-testid="support-tabs">
        {[
          { id: "chat", label: "Chat", icon: MessageCircleQuestion },
          { id: "kb", label: "Base de connaissance", icon: BookOpen },
          { id: "ticket", label: "Ticket", icon: Ticket },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); if (t.id === "ticket") setShowTicketForm(true); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
              tab === t.id
                ? "text-[var(--oh-accent-text)] border-b-2 border-[var(--oh-accent)] bg-[var(--oh-bg-elevated)]"
                : "text-[var(--oh-text-muted)] hover:text-[var(--oh-text-secondary)]"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: CHAT ═══ */}
      {tab === "chat" && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex flex-col ${msg.rôle === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.rôle === "user"
                      ? "bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] rounded-br-sm"
                      : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] border border-[var(--oh-border)] rounded-bl-sm"
                  }`}
                >
                  {msg.content}
                </div>
                {/* Suggestions BDC : articles pertinents avant création de ticket */}
                {msg.rôle === "assistant" && (msg.kb_suggestions || []).length > 0 && (
                  <div className="max-w-[85%] mt-2 space-y-1.5" data-testid={`kb-suggestions-${i}`}>
                    <p className="text-[10px] uppercase tracking-wide text-[var(--oh-text-muted)] font-medium flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      Articles qui pourraient aider
                    </p>
                    {msg.kb_suggestions.map((art) => (
                      <button
                        key={art.id || art.slug || art.title}
                        onClick={() => { setTab("kb"); selectArticle(art); }}
                        className="w-full text-left px-3 py-2 rounded-lg bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] hover:border-[var(--oh-accent)] hover:bg-[var(--oh-accent-bg)] transition-colors"
                        data-testid={`kb-suggestion-${art.id || art.slug}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-[var(--oh-accent-text)] line-clamp-1">{art.title}</p>
                            {art.excerpt && (
                              <p className="text-[11px] text-[var(--oh-text-muted)] line-clamp-2 mt-0.5">{art.excerpt}</p>
                            )}
                          </div>
                          <ArrowRight className="w-3 h-3 text-[var(--oh-accent-text)] mt-0.5 shrink-0" />
                        </div>
                      </button>
                    ))}
                    <button
                      onClick={() => { setShowTicketForm(true); setTab("ticket"); }}
                      className="w-full text-left px-3 py-1.5 rounded-lg text-[11px] text-[var(--oh-text-muted)] hover:text-[var(--oh-text-secondary)] transition-colors"
                    >
                      Aucun de ces articles ne résout mon problème → créer un ticket
                    </button>
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] px-3 py-2 rounded-xl rounded-bl-sm text-sm text-[var(--oh-text-secondary)]">
                  <span className="animate-pulse">En train de reflechir...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t border-[var(--oh-border)] flex gap-2 flex-shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Posez votre question..."
              disabled={sending}
              className="flex-1 px-3 py-2 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg text-sm text-[var(--oh-text)] placeholder-zinc-500 focus:outline-none focus:border-[var(--oh-accent)]"
              data-testid="support-chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              size="icon"
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] h-9 w-9"
              data-testid="support-chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}

      {/* ═══ TAB: KB ═══ */}
      {tab === "kb" && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {kbSelected ? (
            /* Article detail — rendu riche (tutos) ou brut (articles classiques) */
            <div className="flex-1 overflow-y-auto p-4" data-testid="kb-article-detail">
              <button
                onClick={() => { setKbSelected(null); setKbDetail(null); }}
                className="flex items-center gap-1 text-xs text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] mb-3"
                data-testid="kb-back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Retour
              </button>

              {kbDetailLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-5 h-5 border-2 border-[var(--oh-border)] border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-xs text-[var(--oh-text-muted)]">Chargement...</p>
                </div>
              ) : kbDetail?.etapes ? (
                /* ─── Rendu Tuto (étapes, tableaux, attention, astuce) ─── */
                <div className="space-y-3">
                  <h3 className="text-base font-medium text-[var(--oh-text)]" data-testid="kb-detail-title">{kbDetail.title}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {kbDetail.duree && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" data-testid="kb-detail-duree">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round"/></svg>
                        {kbDetail.duree}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]">
                      {kbDetail.target_profile || "guide"}
                    </span>
                  </div>
                  {kbDetail.equivalent_qhare && (
                    <p className="text-xs italic text-[var(--oh-text-muted)] px-2 py-1.5 bg-[var(--oh-bg-elevated)] rounded border border-[var(--oh-border)]" data-testid="kb-detail-qhare">
                      Avant : {kbDetail.equivalent_qhare}
                    </p>
                  )}
                  {/* Étapes numérotées */}
                  <div data-testid="kb-detail-etapes">
                    <p className="text-[10px] uppercase font-medium tracking-wider text-[var(--oh-text-muted)] mb-2">Étapes</p>
                    <ol className="space-y-2">
                      {kbDetail.etapes.map((etape, idx) => (
                        <li key={idx} className="flex gap-2.5 items-start">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--oh-accent-solid)] flex items-center justify-center text-[10px] font-medium text-[var(--oh-accent-contrast)] mt-0.5">
                            {idx + 1}
                          </span>
                          <p className="text-sm text-[var(--oh-text-secondary)] leading-relaxed">{etape}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                  {/* Tableaux */}
                  {kbDetail.tables?.length > 0 && kbDetail.tables.map((table, tIdx) => (
                    <div key={tIdx} className="overflow-x-auto" data-testid={`kb-detail-table-${tIdx}`}>
                      <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {table.headers.map((h, i) => (
                              <th key={i} className="text-left px-2 py-1.5 text-[10px] font-medium uppercase tracking-wider text-[var(--oh-text-muted)] border-b border-[var(--oh-border)] bg-[var(--oh-bg-elevated)]">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, rIdx) => (
                            <tr key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <td key={cIdx} className="px-2 py-1.5 text-[var(--oh-text-secondary)] border-b border-[var(--oh-border)]">{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {/* Attention */}
                  {kbDetail.attention && (
                    <div className="rounded-lg px-3 py-2.5 flex gap-2.5 items-start bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]" data-testid="kb-detail-attention">
                      <svg className="w-4 h-4 text-[var(--oh-info-text)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
                      <p className="text-xs text-[var(--oh-info-text)] leading-relaxed">{kbDetail.attention}</p>
                    </div>
                  )}
                  {/* Astuce */}
                  {kbDetail.astuce && (
                    <div className="rounded-lg px-3 py-2.5 flex gap-2.5 items-start bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" data-testid="kb-detail-astuce">
                      <svg className="w-4 h-4 text-[var(--oh-success-text)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                      <p className="text-xs text-[var(--oh-success-text)] leading-relaxed">{kbDetail.astuce}</p>
                    </div>
                  )}
                </div>
              ) : (
                /* ─── Rendu article classique (content brut) ─── */
                <div>
                  <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] mb-2">
                    {KB_CATEGORY_LABELS[kbDetail?.category] || kbDetail?.category}
                  </span>
                  <h3 className="text-base font-medium text-[var(--oh-text)] mb-3" data-testid="kb-detail-title">{kbDetail?.title}</h3>
                  <div className="text-sm text-[var(--oh-text-secondary)] leading-relaxed whitespace-pre-wrap">
                    {kbDetail?.content}
                  </div>
                  {kbDetail?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-4">
                      {kbDetail.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)]">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Article list */
            <>
              <div className="p-3 border-b border-[var(--oh-border)] space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={kbSearch}
                    onChange={(e) => setKbSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleKbSearch()}
                    placeholder="Rechercher..."
                    className="flex-1 px-3 py-1.5 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg text-sm text-[var(--oh-text)] placeholder-zinc-500 focus:outline-none focus:border-[var(--oh-accent)]"
                    data-testid="kb-search-input"
                  />
                  <Button onClick={handleKbSearch} size="icon" className="bg-[var(--oh-bg-elevated)] hover:bg-zinc-600 h-8 w-8" data-testid="kb-search-btn" aria-label="Rechercher dans la base de connaissances">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <button
                    onClick={() => setKbCategory(null)}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                      !kbCategory ? "bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] hover:bg-[var(--oh-bg-elevated)]"
                    }`}
                    data-testid="kb-filter-all"
                  >
                    Tout
                  </button>
                  {Object.entries(KB_CATEGORY_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setKbCategory(kbCategory === key ? null : key)}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        kbCategory === key ? "bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] hover:bg-[var(--oh-bg-elevated)]"
                      }`}
                      data-testid={`kb-filter-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto" data-testid="kb-articles-list">
                {kbLoading ? (
                  <p className="text-center text-[var(--oh-text-muted)] text-sm py-8 animate-pulse">Chargement...</p>
                ) : kbArticles.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <BookOpen className="w-8 h-8 text-[var(--oh-text-muted)] mx-auto mb-2" />
                    <p className="text-sm text-[var(--oh-text-muted)]">Aucun article pour le moment</p>
                    <p className="text-xs text-[var(--oh-text-muted)] mt-1">La base sera alimentee prochainement</p>
                  </div>
                ) : (
                  kbArticles.map((art) => (
                    <button
                      key={art.id || art.slug}
                      onClick={() => selectArticle(art)}
                      className="w-full text-left px-4 py-3 border-b border-[var(--oh-border)] hover:bg-[var(--oh-bg-elevated)] transition-colors flex items-center gap-3"
                      data-testid={`kb-article-${art.slug || art.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--oh-text-secondary)] truncate">{art.title}</p>
                        <p className="text-xs text-[var(--oh-text-muted)] truncate mt-0.5">
                          {art.etapes ? `${art.duree || ""} — ${art.etapes.length} étapes` : art.content?.substring(0, 80) + "..."}
                        </p>
                      </div>
                      {art.duree && (
                        <span className="text-[10px] text-[var(--oh-text-muted)] shrink-0">{art.duree}</span>
                      )}
                      <ChevronRight className="w-4 h-4 text-[var(--oh-text-muted)] shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ TAB: TICKET ═══ */}
      {tab === "ticket" && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ticket-form">
          <p className="text-sm text-[var(--oh-text-secondary)] mb-2">
            Decrivez votre probleme et nous le transmettrons au responsable concerne.
          </p>

          <input
            type="text"
            value={ticketTitle}
            onChange={(e) => setTicketTitle(e.target.value)}
            placeholder="Titre du probleme..."
            className="w-full px-3 py-2 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg text-sm text-[var(--oh-text)] placeholder-zinc-500 focus:outline-none focus:border-[var(--oh-accent)]"
            data-testid="ticket-title-input"
          />

          <div className="space-y-1.5">
            <label className="text-xs text-[var(--oh-text-secondary)]">Categorie (routage automatique)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setTicketCategory(cat.id)}
                  className={`px-2 py-1.5 rounded-lg text-xs text-left transition-all border ${
                    ticketCategory === cat.id
                      ? "bg-[var(--oh-bg-elevated)] border-[var(--oh-accent)] text-[var(--oh-text)]"
                      : "bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text-secondary)] hover:border-[var(--oh-border)]"
                  }`}
                  data-testid={`ticket-cat-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--oh-bg-elevated)] rounded-lg border border-[var(--oh-border)]">
            <ArrowRight className={`w-3.5 h-3.5 ${selectedCat.color}`} />
            <span className="text-xs text-[var(--oh-text-secondary)]">
              Transmis au <span className={`font-medium ${selectedCat.color}`}>{selectedCat.cxo}</span> + notification admin
            </span>
          </div>

          <div className="flex gap-2">
            <select
              value={ticketPriority}
              onChange={(e) => setTicketPriority(e.target.value)}
              className="px-3 py-2 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg text-sm text-[var(--oh-text)] focus:outline-none focus:border-[var(--oh-accent)]"
              data-testid="ticket-priority-select"
            >
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
              <option value="critical">Critique</option>
            </select>
            <Button
              onClick={createTicket}
              className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-[var(--oh-accent-contrast)] text-sm"
              data-testid="create-ticket-btn"
            >
              <Ticket className="w-4 h-4 mr-1" />
              Créer le ticket
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
